import React from 'react';
import styled from 'styled-components';

/**
 * Props for the ErrorMessage component.
 */
interface ErrorMessageProps {
  message: string;
  details?: string;
  onRetry?: () => void;
}

/**
 * ErrorMessage component.
 * Displays an error message with optional details and retry button.
 */
const ErrorMessage: React.FC<ErrorMessageProps> = ({ message, details, onRetry }) => {
  return (
    <ErrorContainer>
      <ErrorIcon>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="#e74c3c"/>
        </svg>
      </ErrorIcon>
      <ErrorTitle>{message}</ErrorTitle>
      {details && <ErrorDetails>{details}</ErrorDetails>}
      {onRetry && (
        <RetryButton onClick={onRetry}>
          Try Again
        </RetryButton>
      )}
    </ErrorContainer>
  );
};

/**
 * Styled container for the error message.
 */
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1.5rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  max-width: 400px;
  width: 100%;
`;

/**
 * Styled error icon.
 */
const ErrorIcon = styled.div`
  margin-bottom: 1rem;
`;

/**
 * Styled error title.
 */
const ErrorTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.danger};
  margin: 0 0 0.5rem 0;
`;

/**
 * Styled error details.
 */
const ErrorDetails = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 1rem 0;
  max-height: 100px;
  overflow-y: auto;
`;

/**
 * Styled retry button.
 */
const RetryButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.radii.md};
  padding: 0.5rem 1rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 1rem;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark};
  }
`;

export default ErrorMessage; 